import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth';
import { useLanguage, languages } from '../i18n';
import { CraftFoodLogo, LavashLogo, VeloraLogo } from '../components/brand/Logos';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Globe, ArrowRight, ArrowLeft } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const currentLang = languages.find(l => l.code === language);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(t('welcome'));
      if (user.role === 'super_admin' || user.role === 'admin') navigate('/admin');
      else if (user.role === 'operations_manager') navigate('/operations');
      else navigate('/client');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F6F4EE]">
      {/* Left - Brand showcase */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1A1A1A] text-[#F6F4EE] relative overflow-hidden">
        <div className="absolute inset-0 bg-grain pointer-events-none" />
        <div className="relative z-10 w-full flex flex-col justify-between p-12 xl:p-16">
          <Link to="/" data-testid="login-back-home" className="inline-flex items-center gap-2 text-sm text-[#F6F4EE]/60 hover:text-[#F6F4EE] transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>

          <div>
            <CraftFoodLogo size="lg" linkTo={null} dark />
            <h2 className="h-display text-5xl xl:text-6xl mt-12 mb-6 text-[#F6F4EE]">
              {t('loginAside')}
            </h2>
            <p className="text-[#F6F4EE]/60 max-w-md leading-relaxed">
              {t('loginAsideSub')}
            </p>
          </div>

          <div className="flex gap-3">
            <div className="bg-[#556B2F] rounded-lg px-5 py-4 flex-1">
              <LavashLogo size="sm" dark />
            </div>
            <div className="bg-[#8E2A4E] rounded-lg px-5 py-4 flex-1">
              <VeloraLogo size="sm" dark />
            </div>
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-12 lg:hidden">
            <CraftFoodLogo size="sm" />
            <Link to="/" className="text-sm text-[#1A1A1A]/60 hover:text-[#1A1A1A]"><ArrowLeft className="h-4 w-4" /></Link>
          </div>

          <div className="flex justify-end mb-8">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="login-lang-selector" className="gap-1.5 cff-button-outline">
                  <Globe className="h-4 w-4" />
                  {currentLang?.flag} {currentLang?.name}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map(lang => (
                  <DropdownMenuItem key={lang.code} onClick={() => setLanguage(lang.code)} data-testid={`login-lang-${lang.code}`}>
                    <span className="mr-2">{lang.flag}</span> {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <p className="eyebrow mb-4">{t('portalTitle')}</p>
          <h2 className="h-display text-4xl md:text-5xl mb-12">{t('login')}</h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="eyebrow">{t('email')}</Label>
              <Input
                type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="hello@craftfood.kr"
                data-testid="login-email"
                className="mt-2 h-12 bg-transparent border-[#1A1A1A]/20 focus:ring-[#556B2F]"
              />
            </div>
            <div>
              <Label className="eyebrow">{t('password')}</Label>
              <Input
                type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password"
                className="mt-2 h-12 bg-transparent border-[#1A1A1A]/20 focus:ring-[#556B2F]"
              />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-12 cff-button-primary mt-2 text-base">
              {loading ? '...' : <>{t('signIn')} <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </form>

          <div className="mt-10 pt-8 border-t border-[#1A1A1A]/10 text-center">
            <p className="text-sm text-[#1A1A1A]/60 mb-3">{t('register')}?</p>
            <Link to="/register" data-testid="go-to-register" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A1A1A] hover:text-[#556B2F]">
              {t('register')} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
